import React from 'react';
import '../styles/theme.css';

/*
  Hilma af Klint-inspired symbolic plates, in the Policy Pulse brand palette.
  Each plate is a self-contained SVG that fills its parent container.

  Palette (must match theme.css):
    paper     #f4ecd6
    ink       #0e0e10
    blue      #1F4E79
    blueDeep  #143452
    yellow    #FFC700
    rust      #C9472A
    cream     #ece0c4
*/

const BRAND = {
  paper:     '#f4ecd6',
  ink:       '#0e0e10',
  blue:      '#1F4E79',
  blueDeep:  '#143452',
  yellow:    '#FFC700',
  rust:      '#C9472A',
  cream:     '#ece0c4',
};

const PAPER_STYLE = {
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  background: BRAND.paper,
};

// shared paper-grain backdrop (subtle noise filter)
function PaperGrain({ id }) {
  return (
    <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.5 }}>
      <defs>
        <filter id={id}>
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix values="0 0 0 0 0.17 0 0 0 0 0.14 0 0 0 0 0.09 0 0 0 0.18 0" />
        </filter>
      </defs>
      <rect width="100%" height="100%" filter={`url(#${id})`} />
    </svg>
  );
}

const SVG_ATTRS = {
  viewBox: '0 0 400 320',
  preserveAspectRatio: 'xMidYMid slice',
  style: { position: 'absolute', inset: 0, width: '100%', height: '100%' },
};

/* I. POLICY EXPERTISE — ascending arches over a vessel/seed */
export function PolicyPlate() {
  return (
    <div style={PAPER_STYLE}>
      <PaperGrain id="pp-grain-policy" />
      <svg {...SVG_ATTRS}>
        <circle cx="200" cy="180" r="120" fill={BRAND.yellow} opacity="0.27" />
        <circle cx="200" cy="180" r="95"  fill="none" stroke={BRAND.ink} strokeWidth="0.8" />
        <circle cx="200" cy="180" r="72"  fill="none" stroke={BRAND.ink} strokeWidth="0.8" strokeDasharray="2 3" />

        <path d="M 90 240 Q 200 30 310 240"   fill="none" stroke={BRAND.blueDeep} strokeWidth="1.8" />
        <path d="M 110 240 Q 200 60 290 240"  fill="none" stroke={BRAND.yellow}   strokeWidth="1.8" />
        <path d="M 130 240 Q 200 90 270 240"  fill="none" stroke={BRAND.rust}     strokeWidth="1.5" />
        <path d="M 150 240 Q 200 120 250 240" fill="none" stroke={BRAND.yellow}   strokeWidth="1.5" />
        <path d="M 170 240 Q 200 150 230 240" fill="none" stroke={BRAND.blueDeep} strokeWidth="1.5" />

        <line x1="200" y1="30" x2="200" y2="270" stroke={BRAND.ink} strokeWidth="0.6" strokeDasharray="1 4" />

        <ellipse cx="200" cy="245" rx="22" ry="14" fill={BRAND.blueDeep} opacity="0.57" />
        <ellipse cx="200" cy="245" rx="22" ry="14" fill="none" stroke={BRAND.ink} strokeWidth="1" />
        <path d="M 200 245 L 200 200" stroke={BRAND.ink} strokeWidth="0.6" />

        <path d="M 200 200 Q 188 192 192 178 Q 200 184 200 200 Z" fill={BRAND.rust} opacity="0.57" />
        <path d="M 200 200 Q 212 192 208 178 Q 200 184 200 200 Z" fill={BRAND.rust} opacity="0.57" />

        <line x1="60" y1="270" x2="340" y2="270" stroke={BRAND.ink} strokeWidth="0.6" />

        <text x="92"  y="288"                  fontFamily="'IBM Plex Mono', monospace" fontSize="8" fill={BRAND.ink} letterSpacing="3">I.</text>
        <text x="308" y="288" textAnchor="end" fontFamily="'IBM Plex Mono', monospace" fontSize="8" fill={BRAND.ink} letterSpacing="3">POLICY</text>
      </svg>
    </div>
  );
}

/* II. DATA ANALYTICS — three overlapping discs */
export function DataPlate() {
  return (
    <div style={PAPER_STYLE}>
      <PaperGrain id="pp-grain-data" />
      <svg {...SVG_ATTRS}>
        <circle cx="200" cy="160" r="118" fill="none" stroke={BRAND.ink} strokeWidth="0.8" />
        <circle cx="200" cy="160" r="118" fill={BRAND.blue} opacity="0.12" />

        <circle cx="160" cy="140" r="62" fill={BRAND.yellow}   opacity="0.44" />
        <circle cx="240" cy="140" r="62" fill={BRAND.rust}     opacity="0.36" />
        <circle cx="200" cy="200" r="62" fill={BRAND.blueDeep} opacity="0.32" />

        <circle cx="160" cy="140" r="62" fill="none" stroke={BRAND.ink} strokeWidth="0.9" />
        <circle cx="240" cy="140" r="62" fill="none" stroke={BRAND.ink} strokeWidth="0.9" />
        <circle cx="200" cy="200" r="62" fill="none" stroke={BRAND.ink} strokeWidth="0.9" />

        <polygon points="160,140 240,140 200,200" fill="none" stroke={BRAND.ink} strokeWidth="0.6" strokeDasharray="2 3" />

        {[
          [180, 150, BRAND.ink],    [210, 138, BRAND.ink],    [230, 165, BRAND.ink],
          [195, 185, BRAND.yellow], [165, 170, BRAND.ink],    [220, 155, BRAND.ink],
          [188, 158, BRAND.ink],    [205, 172, BRAND.yellow], [218, 180, BRAND.ink],
        ].map(([x, y, c], i) => (
          <circle key={i} cx={x} cy={y} r="1.8" fill={c} />
        ))}

        <text x="60"  y="295"                  fontFamily="'IBM Plex Mono', monospace" fontSize="8" fill={BRAND.ink} letterSpacing="3">II.</text>
        <text x="340" y="295" textAnchor="end" fontFamily="'IBM Plex Mono', monospace" fontSize="8" fill={BRAND.ink} letterSpacing="3">DATA</text>
      </svg>
    </div>
  );
}

/* III. STRATEGIC ADVISORY — compass rose */
export function StrategyPlate() {
  return (
    <div style={PAPER_STYLE}>
      <PaperGrain id="pp-grain-strategy" />
      <svg {...SVG_ATTRS}>
        <circle cx="200" cy="170" r="110" fill={BRAND.cream} opacity="0.6" />
        <circle cx="200" cy="170" r="110" fill="none" stroke={BRAND.ink} strokeWidth="0.8" />
        <circle cx="200" cy="170" r="85"  fill="none" stroke={BRAND.ink} strokeWidth="0.5" strokeDasharray="1 3" />
        <circle cx="200" cy="170" r="42"  fill={BRAND.yellow} opacity="0.44" />
        <circle cx="200" cy="170" r="42"  fill="none" stroke={BRAND.ink} strokeWidth="0.8" />

        {[0, 90, 180, 270].map((deg) => (
          <g key={deg} transform={`rotate(${deg} 200 170)`}>
            <path d="M 200 170 L 192 100 L 200 60 L 208 100 Z" fill={BRAND.blueDeep} opacity="0.32" />
            <path d="M 200 170 L 192 100 L 200 60 L 208 100 Z" fill="none" stroke={BRAND.ink} strokeWidth="0.6" />
          </g>
        ))}
        {[45, 135, 225, 315].map((deg) => (
          <g key={deg} transform={`rotate(${deg} 200 170)`}>
            <path d="M 200 170 L 195 120 L 200 90 L 205 120 Z" fill={BRAND.rust} opacity="0.36" />
            <path d="M 200 170 L 195 120 L 200 90 L 205 120 Z" fill="none" stroke={BRAND.ink} strokeWidth="0.6" />
          </g>
        ))}

        <circle cx="200" cy="170" r="4" fill={BRAND.ink} opacity="0.9" />

        <text x="200" y="48"  textAnchor="middle" fontFamily="'IBM Plex Mono', monospace" fontSize="9" fill={BRAND.ink} letterSpacing="2">N</text>
        <text x="200" y="300" textAnchor="middle" fontFamily="'IBM Plex Mono', monospace" fontSize="9" fill={BRAND.ink} letterSpacing="2">S</text>
        <text x="74"  y="174"                     fontFamily="'IBM Plex Mono', monospace" fontSize="9" fill={BRAND.ink} letterSpacing="2">W</text>
        <text x="320" y="174"                     fontFamily="'IBM Plex Mono', monospace" fontSize="9" fill={BRAND.ink} letterSpacing="2">E</text>

        <text x="60"  y="295"                  fontFamily="'IBM Plex Mono', monospace" fontSize="8" fill={BRAND.ink} letterSpacing="3">III.</text>
        <text x="340" y="295" textAnchor="end" fontFamily="'IBM Plex Mono', monospace" fontSize="8" fill={BRAND.ink} letterSpacing="3">STRATEGY</text>
      </svg>
    </div>
  );
}

const plates = { PolicyPlate, DataPlate, StrategyPlate };
export default plates;
